import heic2any from "heic2any";

/** Timeout in ms for HEIC conversion (avoids hanging on Windows/Chrome). */
const HEIC_CONVERT_TIMEOUT_MS = 20000;

/**
 * Wraps a promise with a timeout. Rejects with a clear error if the promise doesn't resolve in time.
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} message
 * @returns {Promise<T>}
 */
function withTimeout(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * MIME types that are image/* but cannot be displayed reliably in browser <img>
 * (e.g. HEIC from iOS, SVG which we convert to PNG for consistency).
 */
const NON_DISPLAYABLE_IMAGE_TYPES = [
  "image/heic",
  "image/heif",
  "image/svg+xml"
];

/**
 * Returns true if the given MIME type is a displayable image (browsers can show it in <img>).
 */
export function isDisplayableImageType(mimeType) {
  if (!mimeType || !mimeType.startsWith("image/")) return false;
  return !NON_DISPLAYABLE_IMAGE_TYPES.includes(mimeType.toLowerCase());
}

/** True if file has .heic or .heif extension (Windows often reports wrong/empty type). */
function isHeicHeifByExtension(file) {
  const name =
    file && file.name && typeof file.name === "string"
      ? file.name.toLowerCase()
      : "";
  return /\.(heic|heif)$/.test(name);
}

/**
 * Returns true if the file is an image type we convert (HEIC/HEIF or SVG).
 * Includes HEIC/HEIF by file extension so Windows (wrong/empty file.type) is handled.
 */
export function isNonDisplayableImageFile(file) {
  if (!file) return false;
  if (
    file.type &&
    NON_DISPLAYABLE_IMAGE_TYPES.includes(file.type.toLowerCase())
  )
    return true;
  return isHeicHeifByExtension(file);
}

/**
 * Converts SVG markup (string) or SVG File to a PNG blob URL via canvas.
 * @param {string|File} svgInput - SVG as string or File
 * @returns {Promise<string>} - Blob URL of the PNG (call URL.revokeObjectURL when done)
 */
function svgToPngBlobUrl(svgInput) {
  return new Promise((resolve, reject) => {
    const getSvgString = () => {
      if (typeof svgInput === "string") return Promise.resolve(svgInput);
      if (svgInput && typeof svgInput.text === "function")
        return svgInput.text();
      return Promise.reject(new Error("Invalid SVG input"));
    };

    getSvgString()
      .then((svgString) => {
        if (!svgString || !svgString.trim().toLowerCase().includes("<svg")) {
          reject(new Error("Invalid SVG content"));
          return;
        }
        const blob = new Blob([svgString], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          try {
            const w = img.naturalWidth || 300;
            const h = img.naturalHeight || 300;
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
              URL.revokeObjectURL(url);
              reject(new Error("Canvas 2d context not available"));
              return;
            }
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob(
              (pngBlob) => {
                if (!pngBlob) {
                  reject(new Error("Canvas toBlob failed"));
                  return;
                }
                resolve(URL.createObjectURL(pngBlob));
              },
              "image/png",
              1
            );
          } catch (e) {
            URL.revokeObjectURL(url);
            reject(e);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Failed to load SVG image"));
        };
        img.src = url;
      })
      .catch(reject);
  });
}

/**
 * Converts an SVG File to a PNG File (for upload/preview consistency with other images).
 * @param {File} file - SVG file
 * @returns {Promise<File>}
 */
export async function convertSvgToRasterFile(file) {
  if (!file || !file.type) return file;
  const mime = file.type.toLowerCase();
  if (mime !== "image/svg+xml") return file;

  const blobUrl = await svgToPngBlobUrl(file);
  try {
    const res = await fetch(blobUrl);
    if (!res.ok)
      throw new Error(`SVG conversion failed: ${res.statusText || res.status}`);
    const blob = await res.blob();
    const name = (
      file.name && typeof file.name === "string" ? file.name : "image.svg"
    ).replace(/\.svg$/i, ".png");
    return new File([blob], name, {
      type: "image/png",
      lastModified: Date.now()
    });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Fetches an SVG from a URL and converts it to a PNG blob URL for display.
 * Use this when you only have a URL (e.g. in attachment grid/dialog). Revoke the returned URL when no longer needed.
 * @param {string} url - URL of the SVG (blob:, http:, or data:)
 * @returns {Promise<string>} - Blob URL of the PNG
 */
export async function convertSvgUrlToRasterUrl(url) {
  if (!url || typeof url !== "string") throw new Error("Invalid URL");
  const isDataUrl = url.startsWith("data:");
  const svgString = isDataUrl
    ? decodeSvgFromDataUrl(url)
    : await fetch(url).then((r) => {
        if (!r.ok) throw new Error(r.statusText);
        return r.text();
      });
  return svgToPngBlobUrl(svgString);
}

function decodeSvgFromDataUrl(dataUrl) {
  const match = dataUrl.match(/^data:image\/svg\+xml(;base64)?,([\s\S]+)$/);
  if (!match) throw new Error("Invalid SVG data URL");
  const raw = match[2];
  return match[1] ? atob(raw.replace(/\s/g, "")) : decodeURIComponent(raw);
}

/**
 * Converts HEIC/HEIF or SVG to a displayable format (File). SVG becomes PNG; HEIC becomes JPEG.
 * If the file is already displayable, returns it unchanged.
 */
export async function convertToDisplayableImageFile(file, options = {}) {
  const { outputType = "image/jpeg", quality = 0.92 } = options;

  if (!file) return file;

  const mime = (file.type && file.type.toLowerCase()) || "";
  const isHeicByExtension = isHeicHeifByExtension(file);

  if (mime === "image/svg+xml") {
    return convertSvgToRasterFile(file);
  }

  // HEIC/HEIF by MIME or by extension (Windows often reports empty or application/octet-stream)
  const needsHeicConversion =
    mime === "image/heic" || mime === "image/heif" || isHeicByExtension;
  if (!needsHeicConversion) {
    return file;
  }

  const timeoutMsg =
    "HEIC conversion is taking too long. Try a smaller image or save as JPEG on your device.";
  const genericMsg =
    "HEIC conversion failed. Try saving the photo as JPEG on your device or use a different image.";

  try {
    // Prefer heic-convert (libheif-js). On Windows, PNG encoding often fails — we fall back to JPEG then.
    const buffer = new Uint8Array(await file.arrayBuffer());
    const requestedFormat = outputType === "image/png" ? "PNG" : "JPEG";

    let convertedBuffer;
    let actualOutputType = outputType;
    try {
      convertedBuffer = await withTimeout(
        convertHeicWithHeicConvert(buffer, requestedFormat, quality),
        HEIC_CONVERT_TIMEOUT_MS,
        timeoutMsg
      );
    } catch {
      // On Windows, HEIC→PNG often fails (libheif PNG encoder). Retry with JPEG for a working image.
      if (requestedFormat === "PNG") {
        try {
          convertedBuffer = await withTimeout(
            convertHeicWithHeicConvert(buffer, "JPEG", quality),
            HEIC_CONVERT_TIMEOUT_MS,
            timeoutMsg
          );
          actualOutputType = "image/jpeg";
        } catch {
          // Continue to heic2any fallback below
        }
      }
      // Fallback to heic2any if heic-convert failed (e.g. load/decode error or Windows PNG)
      if (!convertedBuffer || convertedBuffer.length === 0) {
        try {
          const blob = await withTimeout(
            convertHeicWithHeic2any(file, actualOutputType, quality),
            HEIC_CONVERT_TIMEOUT_MS,
            timeoutMsg
          );
          convertedBuffer = await blobToUint8Array(blob);
        } catch {
          // Leave convertedBuffer unset; unified check below will throw genericMsg
        }
      }
    }

    if (!convertedBuffer || convertedBuffer.length === 0) {
      throw new Error(genericMsg);
    }

    // Normalize to Uint8Array (heic-convert may return Buffer/ArrayBuffer in some environments)
    const bytes =
      convertedBuffer instanceof Uint8Array
        ? convertedBuffer
        : new Uint8Array(convertedBuffer);

    const blob = new Blob([bytes], { type: actualOutputType });
    const ext = actualOutputType === "image/png" ? "png" : "jpeg";
    const baseName =
      file.name && typeof file.name === "string" ? file.name : "image.heic";
    const newName = baseName.replace(/\.(heic|heif)$/i, `.${ext}`);
    return new File([blob], newName, {
      type: actualOutputType,
      lastModified: Date.now()
    });
  } catch (err) {
    const message = err && err.message ? err.message : genericMsg;
    throw new Error(message);
  }
}

/**
 * Converts HEIC buffer to JPEG or PNG using heic-convert (browser), which works on Windows/Chrome.
 * @param {Uint8Array} buffer - HEIC file bytes
 * @param {string} format - 'JPEG' or 'PNG'
 * @param {number} quality - 0–1 for JPEG
 * @returns {Promise<Uint8Array>}
 */
async function convertHeicWithHeicConvert(buffer, format, quality) {
  const heicConvert = (await import("heic-convert/browser")).default;
  return heicConvert({ buffer, format, quality });
}

/**
 * Fallback: convert HEIC using heic2any (can hang or fail on Windows/Chrome).
 * @param {File} file
 * @param {string} outputType - 'image/jpeg' or 'image/png'
 * @param {number} quality
 * @returns {Promise<Blob>}
 */
async function convertHeicWithHeic2any(file, outputType, quality) {
  const blobInput = await createBlobFromFile(file);
  const result = await heic2any({
    blob: blobInput,
    toType: outputType,
    quality
  });
  const blob = Array.isArray(result) ? result[0] : result;
  if (!blob || !(blob instanceof Blob) || blob.size === 0) {
    throw new Error("HEIC conversion produced no output.");
  }
  return blob;
}

/**
 * @param {Blob} blob
 * @returns {Promise<Uint8Array>}
 */
async function blobToUint8Array(blob) {
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Creates a plain Blob from a File (used for HEIC conversion so the decoder gets a clean copy).
 * @param {File} file
 * @returns {Promise<Blob>}
 */
async function createBlobFromFile(file) {
  const buffer = await file.arrayBuffer();
  return new Blob([buffer], { type: file.type });
}
